import { useState, useEffect } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import {
  FileText,
  Plus,
  Trash2,
  Edit,
  Upload,
  Search,
  Filter,
  Copy,
  Check,
  Loader2,
  BookOpen,
  Briefcase,
  Code,
  PenTool,
  Scale
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORY_ICONS = {
  legal: Scale,
  service: Briefcase,
  creative: PenTool,
  technical: Code,
  professional: BookOpen,
  custom: FileText
};

const CATEGORY_COLORS = {
  legal: "bg-red-500/20 text-red-400",
  service: "bg-blue-500/20 text-indigo-400",
  creative: "bg-indigo-500/20 text-indigo-400",
  technical: "bg-green-500/20 text-green-400",
  professional: "bg-indigo-500/20 text-indigo-400",
  custom: "bg-slate-500/20 text-indigo-400"
};

export default function ContractTemplates({ onSelectTemplate }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // New template form
  const [newTemplate, setNewTemplate] = useState({
    name: "",
    category: "custom",
    description: "",
    service_type: "general",
    md_body: ""
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await api.get("/contracts/templates");
      setTemplates(res.data.templates || []);
    } catch (e) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const createTemplate = async () => {
    if (!newTemplate.name.trim() || !newTemplate.md_body.trim()) {
      toast.error("Name and content are required");
      return;
    }
    
    setCreating(true);
    try {
      const res = await api.post("/contracts/templates", newTemplate);
      setTemplates([res.data, ...templates]);
      setShowCreateForm(false);
      setNewTemplate({
        name: "",
        category: "custom",
        description: "",
        service_type: "general",
        md_body: ""
      });
      toast.success("Template created!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create template");
    } finally {
      setCreating(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!confirm("Delete this template?")) return;
    
    try {
      await api.delete(`/contracts/templates/${templateId}`);
      setTemplates(templates.filter(t => t.id !== templateId));
      toast.success("Template deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot delete system templates");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Read file content
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result;
      if (typeof content === 'string') {
        setNewTemplate({
          ...newTemplate,
          md_body: content,
          name: file.name.replace(/\.[^/.]+$/, "") // Remove extension
        });
        setShowUploadForm(false);
        setShowCreateForm(true);
        toast.success("File loaded! Review and save your template.");
      }
    };
    reader.readAsText(file);
  };

  const filteredTemplates = templates.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         t.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === "all" || t.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = [...new Set(templates.map(t => t.category))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contract-templates">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-indigo-500 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-indigo-400" />
            Contract Templates
          </h2>
          <p className="text-indigo-400 text-sm mt-1">
            Start with a template or create your own
          </p>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={() => setShowUploadForm(true)}
            className="flex items-center gap-2 px-3 py-2 bg-slate-400/50 hover:bg-slate-600/50 text-indigo-500 rounded-lg transition-colors text-sm"
          >
            <Upload className="w-4 h-4" />
            Upload
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-2 px-3 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-colors text-sm"
          >
            <Plus className="w-4 h-4" />
            Create
          </button>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search templates..."
            className="w-full pl-10 pr-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-indigo-500 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="all" className="text-slate-400">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat} value={cat} className="text-slate-400 capitalize">
                {cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Upload Form */}
      {showUploadForm && (
        <Card className="bg-slate-800/50 border-indigo-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-indigo-500 font-medium">Upload Document as Template</h3>
              <button onClick={() => setShowUploadForm(false)} className="text-indigo-400 hover:text-indigo-500">×</button>
            </div>
            <div className="border-2 border-dashed border-slate-600 rounded-xl p-8 text-center">
              <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <p className="text-indigo-400 mb-2">Upload a text document (.txt, .md)</p>
              <input
                type="file"
                accept=".txt,.md,.markdown"
                onChange={handleFileUpload}
                className="hidden"
                id="template-upload"
              />
              <label
                htmlFor="template-upload"
                className="inline-block px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg cursor-pointer transition-colors"
              >
                Choose File
              </label>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Form */}
      {showCreateForm && (
        <Card className="bg-slate-800/50 border-indigo-500/30">
          <CardHeader>
            <CardTitle className="text-indigo-500">Create Custom Template</CardTitle>
            <CardDescription className="text-indigo-400">
              Build a reusable contract template
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-indigo-400 mb-1">Template Name</label>
                <input
                  type="text"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                  placeholder="e.g., My Service Agreement"
                  className="w-full px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-indigo-400 mb-1">Category</label>
                <Select value={newTemplate.category} onValueChange={(v) => setNewTemplate({ ...newTemplate, category: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="service" className="text-slate-400">Service</SelectItem>
                    <SelectItem value="legal" className="text-slate-400">Legal</SelectItem>
                    <SelectItem value="creative" className="text-slate-400">Creative</SelectItem>
                    <SelectItem value="technical" className="text-slate-400">Technical</SelectItem>
                    <SelectItem value="professional" className="text-slate-400">Professional</SelectItem>
                    <SelectItem value="custom" className="text-slate-400">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-indigo-400 mb-1">Description</label>
              <input
                type="text"
                value={newTemplate.description}
                onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                placeholder="Brief description of this template"
                className="w-full px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
              />
            </div>
            
            <div>
              <label className="block text-sm text-indigo-400 mb-1">Contract Content (Markdown)</label>
              <Textarea
                value={newTemplate.md_body}
                onChange={(e) => setNewTemplate({ ...newTemplate, md_body: e.target.value })}
                placeholder="# Contract Title&#10;&#10;## Section 1&#10;..."
                className="min-h-[300px] font-mono text-sm bg-slate-900/50 border-slate-600 text-indigo-500"
              />
              <p className="text-xs text-slate-400 mt-1">
                Use placeholders like {"{client_name}"}, {"{freelancer_name}"}, {"{project_title}"} for auto-fill
              </p>
            </div>
            
            <div className="flex gap-3 pt-2">
              <button
                onClick={createTemplate}
                disabled={creating}
                className="flex-1 py-3 bg-gradient-to-r from-indigo-500 to-orange-600 hover:from-indigo-600 hover:to-orange-700 text-indigo-500 font-medium rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
                {creating ? "Creating..." : "Create Template"}
              </button>
              <button
                onClick={() => setShowCreateForm(false)}
                className="px-6 py-3 text-indigo-400 hover:text-indigo-500 transition-colors"
              >
                Cancel
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredTemplates.map((template) => {
          const CategoryIcon = CATEGORY_ICONS[template.category] || FileText;
          
          return (
            <Card
              key={template.id}
              className="bg-slate-800/50 border-slate-700/50 hover:border-indigo-500/30 transition-colors cursor-pointer group"
              onClick={() => onSelectTemplate?.(template)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${CATEGORY_COLORS[template.category] || CATEGORY_COLORS.custom}`}>
                      <CategoryIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-medium text-indigo-500 group-hover:text-indigo-400 transition-colors line-clamp-1">
                        {template.name}
                      </h3>
                      <Badge className={`text-xs ${CATEGORY_COLORS[template.category] || CATEGORY_COLORS.custom}`}>
                        {template.category}
                      </Badge>
                    </div>
                  </div>
                  
                  {template.is_system && (
                    <span className="text-xs text-slate-400">System</span>
                  )}
                </div>
                
                <p className="text-sm text-indigo-400 line-clamp-2 mb-3">
                  {template.description || "No description"}
                </p>
                
                <div className="flex items-center justify-between">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectTemplate?.(template);
                    }}
                    className="text-xs text-indigo-400 hover:text-indigo-400 flex items-center gap-1"
                  >
                    <Copy className="w-3 h-3" />
                    Use Template
                  </button>
                  
                  {!template.is_system && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteTemplate(template.id);
                      }}
                      className="text-xs text-slate-400 hover:text-red-400 flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-indigo-400">No templates found</p>
        </div>
      )}
    </div>
  );
}
